import React, { useMemo } from 'react';

import styled from 'styled-components';

import { isAllDay, showTime } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useCloneModelsFollowup, forceReloadFollowup } from '@hooks/API/Event/UseFollowup';
import { forceReloadProject } from '@hooks/API/Project/UseProject';

import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import EventDocuments from '@components/EventDocuments/EventDocuments';
import { EventAddModelQuestion } from '@components/EventDrawer/EventDrawerUi/EventAddModelQuestion';
import { EventCommentsDocuments } from '@components/EventDrawer/EventDrawerUi/EventCommentsDocuments';
import { EventDetailsWrapper, ObjectHistoryContainer } from '@components/EventDrawer/EventDrawerUi/EventUI';
import FollowUpActions from '@components/EventDrawer/FollowUpEvent/FollowUpActions';
import { useFollowUp } from '@components/EventDrawer/FollowUpEvent/FollowUpContext';
import FollowUpDetails from '@components/EventDrawer/FollowUpEvent/FollowUpDetails';
import FollowUpGroupInstanceDetails from '@components/EventDrawer/FollowUpEvent/FollowUpGroupInstanceDetails';
import { FollowUpModelNameAndDate } from '@components/EventDrawer/FollowUpEvent/FollowUpModelNameAndDate';
import { FollowUpTitle } from '@components/EventDrawer/FollowUpEvent/FollowUpTitle';
import FlexContainer from '@components/Gui/FlexContainer';
import ResponsiveFlexContainer from '@components/Gui/ResponsiveFlexContainer';

const Wrapper = styled.div`
    .followup-group-instance-details-wrapper {
        padding-bottom: 3rem;
    }
`;

const FollowUpContent = ({ filterModel, handleSelectProject, onTouchForm, setViewDetails, viewDetails }) => {
    const { groupType, followUpData, afterUpdate, followUpID, plainMode } = useFollowUp();

    const { mutateAsync: onCloneModelsFollowup } = useCloneModelsFollowup();

    const FollowUpInstances = useMemo(() => {
        const events = followUpData.RelatedEvents ? [followUpData, ...followUpData.RelatedEvents] : [followUpData];
        if (events.length <= 1) {
            return events;
        }
        return [...events].sort((a, b) => {
            // Cancelled events go to the bottom
            if (a.Cancel && !b.Cancel) return 1;
            if (!a.Cancel && b.Cancel) return -1;

            if (groupType === 'period') {
                return new Date(a.StartDate) - new Date(b.StartDate);
            } else {
                const nameA = getContactName(a.ModelID) || '';
                const nameB = getContactName(b.ModelID) || '';
                return nameA.localeCompare(nameB);
            }
        });
    }, [followUpData, groupType]);

    // Calcola se description e time sono comuni a tutti gli eventi
    const { isDescriptionCommon, isTimeCommon, commonDescription, commonTimeDisplay } = useMemo(() => {
        if (FollowUpInstances.length <= 1) {
            return {
                isDescriptionCommon: false,
                isTimeCommon: false,
                commonDescription: null,
                commonTimeDisplay: null,
            };
        }

        // Controlla se tutte le description sono uguali
        const firstDescription = FollowUpInstances[0].Description || '';
        const allDescriptionsEqual = FollowUpInstances.every((event) => (event.Description || '') === firstDescription);

        // Controlla se tutti i time sono uguali (solo per groupType = 'models')
        let allTimesEqual = false;
        let timeDisplay = null;
        if (groupType === 'models') {
            const firstStartTime = showTime(FollowUpInstances[0].StartDate);
            const firstEndTime = showTime(FollowUpInstances[0].EndDate);
            const firstIsAllDay = isAllDay(FollowUpInstances[0].StartDate, FollowUpInstances[0].EndDate);

            allTimesEqual = FollowUpInstances.every((event) => {
                const eventIsAllDay = isAllDay(event.StartDate, event.EndDate);
                if (firstIsAllDay && eventIsAllDay) return true;
                if (firstIsAllDay !== eventIsAllDay) return false;
                return showTime(event.StartDate) === firstStartTime && showTime(event.EndDate) === firstEndTime;
            });

            if (allTimesEqual) {
                timeDisplay = firstIsAllDay ? 'All Day' : `${firstStartTime} > ${firstEndTime}`;
            }
        }

        return {
            isDescriptionCommon: allDescriptionsEqual,
            isTimeCommon: allTimesEqual,
            commonDescription: allDescriptionsEqual && firstDescription ? firstDescription : null,
            commonTimeDisplay: timeDisplay,
        };
    }, [FollowUpInstances, groupType]);

    const isIncludeFilterModel = () => {
        let modelEvents = [followUpData];
        if (followUpData.RelatedEvents && followUpData.RelatedEvents.length > 0) {
            modelEvents = [...modelEvents, ...followUpData.RelatedEvents];
        }

        if (!filterModel) {
            return true;
        } else {
            if (modelEvents.find((modelEvent) => modelEvent.ModelID === Number(filterModel))) {
                return true;
            }
            return;
        }
    };

    const handleAddModelToEvent = async (modelId) => {
        const data = {
            EventID: followUpData.ID,
            ModelIDs: [modelId],
        };
        await onCloneModelsFollowup({ data });
        afterUpdate();
        if (followUpData.Project) {
            forceReloadProject(followUpData.Project.ID);
        }
    };

    const getEventCommentsDocumentsProps = (extra, showOnlyContent, showOnlyActions) => {
        return {
            event: { ...followUpData, elType: 'FollowUp', uniqID: `FollowUp-${followUpData.ID}` },
            api: 'FollowUpID',
            type: 'FollowUp',
            contactId: !followUpData.GroupGuid && followUpData.ModelID,
            drawerMode: !plainMode,
            fetchEvent: () => {
                forceReloadFollowup(followUpData.ID);
            },
            extra: extra,
            showOnlyContent: showOnlyContent,
            showOnlyActions: showOnlyActions,
            onTouchForm: onTouchForm,
        };
    };

    return (
        <>
            {isIncludeFilterModel() || viewDetails ? (
                <Wrapper>
                    <FollowUpTitle handleSelectProject={handleSelectProject} />
                    <FollowUpModelNameAndDate />
                    <FollowUpDetails />
                    {groupType && followUpData.GroupDocuments.length > 0 && (
                        <FlexContainer pd="0 20px" column alignItems="flex-start">
                            <EventDocuments
                                showDocumentsByDefault
                                event={followUpData}
                                type={'eventGroup'}
                                updateEvent={() => forceReloadFollowup(followUpData.ID)}
                                singleColumn={true}
                            />
                        </FlexContainer>
                    )}
                    {!groupType && <EventCommentsDocuments {...getEventCommentsDocumentsProps(null, true)} />}
                    <ObjectHistoryContainer>
                        <ObjectHistory asLine object={followUpData} />
                    </ObjectHistoryContainer>
                    {!groupType ? (
                        <EventCommentsDocuments
                            singleColumnDocuments
                            {...getEventCommentsDocumentsProps(<FollowUpActions />, false, true)}
                        />
                    ) : (
                        <FollowUpActions hasBorder />
                    )}
                    <div className="followup-group-instance-details-wrapper">
                        {FollowUpInstances.length > 1 ? (
                            <>
                                {(isDescriptionCommon || isTimeCommon) && (
                                    <div style={{ padding: '0 2rem' }}>
                                        <EventDetailsWrapper>
                                            {groupType === 'models' && isTimeCommon && commonTimeDisplay && (
                                                <ResponsiveFlexContainer
                                                    columns={[
                                                        {
                                                            content: <p className="is-label">Time</p>,
                                                            width: '8rem',
                                                            isBorder: true,
                                                        },
                                                        {
                                                            content: <p className="is-content">{commonTimeDisplay}</p>,
                                                            isBorder: false,
                                                        },
                                                    ]}
                                                />
                                            )}
                                            {isDescriptionCommon && (
                                                <ResponsiveFlexContainer
                                                    columns={[
                                                        {
                                                            content: <p className="is-label">Description</p>,
                                                            width: '8rem',
                                                            isBorder: true,
                                                        },
                                                        {
                                                            content: <p className="is-content">{commonDescription}</p>,
                                                            isBorder: false,
                                                        },
                                                    ]}
                                                />
                                            )}
                                        </EventDetailsWrapper>
                                    </div>
                                )}
                                {FollowUpInstances.map((event) => (
                                    <FollowUpGroupInstanceDetails
                                        key={`event-${event.ID}`}
                                        event={event}
                                        openedEventID={followUpData.ID}
                                        onTouchForm={onTouchForm}
                                        hideDescription={isDescriptionCommon}
                                        hideTime={groupType === 'models' && isTimeCommon}
                                    />
                                ))}
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                </Wrapper>
            ) : (
                <EventAddModelQuestion
                    eventData={followUpData}
                    model={filterModel}
                    onAddModel={(m) => handleAddModelToEvent(m)}
                    onShowEvent={() => setViewDetails(true)}
                />
            )}
        </>
    );
};

export default FollowUpContent;
